// Pure derivations for the Crew Utilization page. No I/O; unit-tested in
// crew-utilization.test.ts. Capture/KPI only — NO pay math.
//
// LF and SF are different units and are NEVER summed here — callers pass one at
// a time. Throughput / footage-per-job operate on a single footage figure.

/** Footage installed per active day = footage ÷ activeDays. 0 active days → 0
 *  (avoids divide-by-zero; an empty period has no throughput). */
export function throughput(footage: number, activeDays: number): number {
  if (activeDays <= 0) return 0;
  return footage / activeDays;
}

/** Average footage per job = footage ÷ jobs. 0 jobs → 0. */
export function avgFootagePerJob(footage: number, jobs: number): number {
  if (jobs <= 0) return 0;
  return footage / jobs;
}

/** Count of workdays in [from, to] inclusive (both YYYY-MM-DD). includeSat
 *  toggles Mon–Sat (true) vs Mon–Fri (false, default). Used as the denominator
 *  for the approximate utilization rate. Pure date arithmetic on LOCAL parts. */
export function workdaysBetween(
  from: string,
  to: string,
  includeSat = false,
): number {
  if (from > to) return 0;
  const start = parseLocal(from);
  const end = parseLocal(to);
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay(); // 0=Sun .. 6=Sat
    if (day >= 1 && (includeSat ? day <= 6 : day <= 5)) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/** Approximate utilization rate (%) = activeDays ÷ workdaysInRange × 100,
 *  CAPPED at 100. Returns null when there are no workdays in the range (rate is
 *  undefined). Architect #3: this is APPROXIMATE — base it on Mon–Sat, cap 100,
 *  always label "approx" in the UI. */
export function utilizationRate(
  activeDays: number,
  workdaysInRange: number,
): number | null {
  if (workdaysInRange <= 0) return null;
  const rate = (activeDays / workdaysInRange) * 100;
  return Math.min(100, rate);
}

/** Parse a YYYY-MM-DD as a LOCAL date (no UTC drift). */
function parseLocal(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
